import React from 'react';
import { Suspect } from '../../types/crime-game';
import { User, ShieldQuestion } from 'lucide-react';

interface SuspectGridProps {
  suspects: Suspect[];
  onSelect: (suspect: Suspect) => void;
  selectedId?: string;
  readOnly?: boolean;
  layout?: 'full' | 'sidebar';
}

export const SuspectGrid: React.FC<SuspectGridProps> = ({
  suspects,
  onSelect,
  selectedId,
  readOnly = false,
  layout = 'full'
}) => {
  // Determine grid columns based on layout
  const gridClasses = layout === 'sidebar'
    ? 'grid-cols-1'
    : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid ${gridClasses} gap-4 w-full max-w-5xl mx-auto my-6`}>
      {suspects.map((suspect) => {
        // Clean up display by hiding default values
        const showMotive = suspect.motive && suspect.motive !== "Unknown";
        const showAlibi = suspect.alibi && suspect.alibi !== "Being verified";

        return (
          <div
            key={suspect.id}
            onClick={() => !readOnly && onSelect(suspect)}
            className={`
              relative p-4 rounded-xl border-2 cursor-pointer transition-all transform hover:shadow-lg
              ${selectedId === suspect.id
                ? 'border-indigo-500 bg-indigo-50 scale-[1.02]'
                : 'border-gray-200 bg-white hover:border-indigo-200'
              }
              ${readOnly ? 'cursor-default' : ''}
            `}
          >
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-gray-100 rounded-full flex-shrink-0">
                <User className="w-6 h-6 text-gray-600" />
              </div>
              <h3 className="font-bold text-lg text-gray-800 leading-tight">{suspect.name}</h3>
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <p className="line-clamp-3"><span className="font-semibold text-gray-700">Bio:</span> {suspect.bio}</p>

              {showMotive && (
                <p><span className="font-semibold text-gray-700">Motive:</span> {suspect.motive}</p>
              )}
              {showAlibi && (
                <p><span className="font-semibold text-gray-700">Alibi:</span> {suspect.alibi}</p>
              )}
            </div>

            {selectedId === suspect.id && !readOnly && (
               <div className="absolute top-2 right-2 text-indigo-500">
                 <ShieldQuestion className="w-6 h-6" />
               </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
